import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent } from '../components/ui/collapsible';
import api from '../lib/api';
import { usePersistentFilters } from '../lib/persistentFilters';
import { toast } from 'sonner';
import { Plus, Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';
import { PermissionGuard } from '../contexts/PermissionsContext';
import { useCompany } from '../contexts/CompanyContext';

const GreyOrdersList = () => {
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = usePersistentFilters('greyOrders.search', '');
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = usePersistentFilters('greyOrders.filters', {
    order_date_from: '',
    order_date_to: '',
    supplier_id: '',
    quality_id: '',
    subquality_id: '',
    item_id: '',
    item: '',
    order_unit: '',
    status: '',
    pending_qty_filter: ''
  });

  // Refetch data when company changes
  useEffect(() => {
    fetchData();
  }, [activeCompany?.company_id]);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, qualitiesRes, subqualitiesRes, itemsRes] = await Promise.allSettled([
        api.get('/grey-orders'),
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities'),
        api.get('/filter-options/items')
      ]);
      setOrders(ordersRes.status === 'fulfilled' ? ordersRes.value.data : []);
      setSuppliers(suppliersRes.status === 'fulfilled' ? suppliersRes.value.data : []);
      setQualities(qualitiesRes.status === 'fulfilled' ? qualitiesRes.value.data : []);
      setSubqualities(subqualitiesRes.status === 'fulfilled' ? subqualitiesRes.value.data : []);
      setItems(itemsRes.status === 'fulfilled' ? itemsRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load orders');
      setLoading(false);
    }
  };

  // Apply filters
  const filteredOrders = orders.filter(order => {
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        order.order_number?.toLowerCase().includes(searchLower) ||
        order.supplier_name?.toLowerCase().includes(searchLower) ||
        order.quality_name?.toLowerCase().includes(searchLower) ||
        order.item?.toLowerCase().includes(searchLower) ||
        order.item_name?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Advanced filters
    if (filters.supplier_id && order.supplier_id !== filters.supplier_id) return false;
    if (filters.quality_id && order.quality_id !== filters.quality_id) return false;
    if (filters.subquality_id && order.subquality_id !== filters.subquality_id) return false;
    if (filters.item_id && order.item_id !== filters.item_id) return false;
    if (filters.item && !(order.item?.toLowerCase().includes(filters.item.toLowerCase()) || order.item_name?.toLowerCase().includes(filters.item.toLowerCase()))) return false;
    if (filters.order_unit && order.order_unit !== filters.order_unit) return false;
    if (filters.status && order.order_status !== filters.status) return false;
    
    // Pending Quantity filter
    if (filters.pending_qty_filter) {
      const pq = order.pending_quantity ?? 0;
      if (filters.pending_qty_filter === 'gt0' && pq <= 0) return false;
      if (filters.pending_qty_filter === 'eq0' && pq !== 0) return false;
    }
    
    // Date range filters
    if (filters.order_date_from) {
      const orderDate = new Date(order.order_date);
      const fromDate = new Date(filters.order_date_from);
      if (orderDate < fromDate) return false;
    }
    if (filters.order_date_to) {
      const orderDate = new Date(order.order_date);
      const toDate = new Date(filters.order_date_to);
      if (orderDate > toDate) return false;
    }
    
    return true;
  });

  const resetFilters = () => {
    setFilters({
      order_date_from: '',
      order_date_to: '',
      supplier_id: '',
      quality_id: '',
      subquality_id: '',
      item_id: '',
      item: '',
      order_unit: '',
      status: '',
      pending_qty_filter: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  // Get unique units from orders
  const uniqueUnits = [...new Set(orders.map(o => o.order_unit).filter(Boolean))];

  const statusColors = {
    'Pending': 'bg-red-50 text-red-700 border-red-200',
    'Partially Billed': 'bg-amber-50 text-amber-700 border-amber-200',
    'Completed': 'bg-teal-50 text-teal-700 border-teal-200'
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="grey-orders-list" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Grey Orders
            </h1>
            <p className="text-slate-600 mt-1">{filteredOrders.length} orders</p>
          </div>
          <PermissionGuard module="grey_orders" permission="create">
            <div className="flex gap-2">
              <Button
                data-testid="bulk-add-orders-btn"
                variant="outline"
                onClick={() => navigate('/bulk/grey-orders')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Bulk Add
              </Button>
              <Button 
                data-testid="create-order-btn"
                onClick={() => navigate('/grey-orders/create')} 
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Order
              </Button>
            </div>
          </PermissionGuard>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-orders-input"
              placeholder="Search orders by number, supplier, quality..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="h-11"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-slate-900 text-white">{activeFilterCount}</Badge>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent>
            <Card className="mb-4 border border-slate-200">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Order Date From</Label>
                    <Input
                      type="date"
                      value={filters.order_date_from}
                      onChange={(e) => setFilters({...filters, order_date_from: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Order Date To</Label>
                    <Input
                      type="date"
                      value={filters.order_date_to}
                      onChange={(e) => setFilters({...filters, order_date_to: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Supplier</Label>
                    <Select value={filters.supplier_id} onValueChange={(v) => setFilters({...filters, supplier_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All suppliers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All suppliers</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Quality</Label>
                    <Select value={filters.quality_id} onValueChange={(v) => setFilters({...filters, quality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All qualities</SelectItem>
                        {qualities.map(q => (
                          <SelectItem key={q.quality_id} value={q.quality_id}>{q.quality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Sub Quality</Label>
                    <Select value={filters.subquality_id} onValueChange={(v) => setFilters({...filters, subquality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All sub qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sub qualities</SelectItem>
                        {subqualities.map(sq => (
                          <SelectItem key={sq.subquality_id} value={sq.subquality_id}>{sq.subquality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Item</Label>
                    <Select value={filters.item_id} onValueChange={(v) => setFilters({...filters, item_id: v === 'all' ? '' : v, item: ''})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All items" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All items</SelectItem>
                        {items.map(i => (
                          <SelectItem key={i.item_id || i.item_name} value={i.item_id || i.item_name}>{i.item_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Order Unit</Label>
                    <Select value={filters.order_unit} onValueChange={(v) => setFilters({...filters, order_unit: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All units" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All units</SelectItem>
                        {uniqueUnits.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Partially Billed">Partially Billed</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Pending Quantity</Label>
                    <Select value={filters.pending_qty_filter} onValueChange={(v) => setFilters({...filters, pending_qty_filter: v === 'all' ? '' : v})}>
                      <SelectTrigger data-testid="filter-pending-qty" className="h-9">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="gt0">Pending Quantity &gt; 0</SelectItem>
                        <SelectItem value="eq0">Pending Quantity = 0</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end mt-4 gap-2">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.supplier_id && (
              <Badge variant="secondary" className="gap-1">
                Supplier: {suppliers.find(s => s.supplier_id === filters.supplier_id)?.name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, supplier_id: ''})} />
              </Badge>
            )}
            {filters.quality_id && (
              <Badge variant="secondary" className="gap-1">
                Quality: {qualities.find(q => q.quality_id === filters.quality_id)?.quality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, quality_id: ''})} />
              </Badge>
            )}
            {filters.subquality_id && (
              <Badge variant="secondary" className="gap-1">
                Sub Quality: {subqualities.find(sq => sq.subquality_id === filters.subquality_id)?.subquality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, subquality_id: ''})} />
              </Badge>
            )}
            {filters.status && (
              <Badge variant="secondary" className="gap-1">
                Status: {filters.status}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, status: ''})} />
              </Badge>
            )}
            {filters.order_unit && (
              <Badge variant="secondary" className="gap-1">
                Unit: {filters.order_unit}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, order_unit: ''})} />
              </Badge>
            )}
            {filters.order_date_from && (
              <Badge variant="secondary" className="gap-1">
                From: {filters.order_date_from}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, order_date_from: ''})} />
              </Badge>
            )}
            {filters.order_date_to && (
              <Badge variant="secondary" className="gap-1">
                To: {filters.order_date_to}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, order_date_to: ''})} />
              </Badge>
            )}
            {filters.item && (
              <Badge variant="secondary" className="gap-1">
                Item: {filters.item}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, item: ''})} />
              </Badge>
            )}
            {filters.pending_qty_filter && (
              <Badge variant="secondary" className="gap-1">
                Pending Quantity: {filters.pending_qty_filter === 'gt0' ? '> 0' : '= 0'}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, pending_qty_filter: ''})} />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Orders List */}
      {filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Card 
              key={order.order_id} 
              data-testid={`order-card-${order.order_number}`}
              className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/grey-orders/${order.order_id}`)}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-lg font-bold text-slate-900">{order.order_number}</span>
                      <Badge className={statusColors[order.order_status]}>
                        {order.order_status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Supplier: </span>
                        <span className="text-slate-900 font-medium">{order.supplier_name}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Item: </span>
                        <span className="text-slate-900 font-medium">{order.item || order.item_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Quality: </span>
                        <span className="text-slate-900 font-medium">{order.quality_name}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Date: </span>
                        <span className="text-slate-900 font-medium">{formatDate(order.order_date)}</span>
                      </div>
                      {order.rate != null && order.rate !== '' && (
                        <div data-testid={`order-rate-${order.order_id}`}>
                          <span className="text-slate-500">Rate: </span>
                          <span className="text-slate-900 font-medium">₹{formatNumber(order.rate)}{order.order_unit ? ` / ${order.order_unit}` : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row md:flex-col md:items-end gap-4 md:gap-2">
                    <div className="flex-1 md:flex-none">
                      <div className="text-sm text-slate-500 mb-1">Ordered</div>
                      <div className="text-xl font-bold text-slate-900">{formatNumber(order.ordered_quantity)} {order.order_unit}</div>
                    </div>
                    <div className="flex-1 md:flex-none">
                      <div className="text-sm text-slate-500 mb-1">Received</div>
                      <div className="text-xl font-bold text-teal-700">{formatNumber(order.total_received_quantity)} {order.order_unit}</div>
                    </div>
                    <div className="flex-1 md:flex-none">
                      <div className="text-sm text-slate-500 mb-1">Pending Quantity</div>
                      <div className="text-xl font-bold text-slate-900">{formatNumber(order.pending_quantity)} {order.order_unit}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="py-16 text-center">
            <div className="text-slate-400">
              <p className="text-lg mb-2">No orders found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GreyOrdersList;
